/**
 * NodeStatsSummary Component
 *
 * Displays compact statistics bar for node mappings in the Precursor/Barrier Review tab.
 * Shows total reports, average confidence, and AI vs Manual breakdown.
 * Prioritizes data density with minimal spacing.
 */

import React from 'react';
import {Chip, Stack, Tooltip} from '@mui/material';
import {NodeMappingStatistics} from '../../../../../../@types/irp-node-mappings';

/**
 * Props for NodeStatsSummary component
 */
export interface NodeStatsSummaryProps {
    statistics: NodeMappingStatistics;
    totalFilteredReports: number;
    loading?: boolean;
}

/**
 * Compact statistics summary bar for node mappings
 */
const NodeStatsSummary: React.FC<NodeStatsSummaryProps> = ({ statistics, totalFilteredReports, loading = false }) => {
    const { totalReports, aiMappingsCount, manualMappingsCount } = statistics;

    // Calculate percentage of filtered reports that flow through this node
    const percentage = totalFilteredReports > 0 ? (totalReports / totalFilteredReports) * 100 : 0;

    // Don't show if no data
    if (!loading && totalReports === 0) {
        return null;
    }

    return (
        <Stack direction="row" spacing={1} alignItems="center">
            {/* Total Reports Chip */}
            <Tooltip title={`${totalReports} out of ${totalFilteredReports} filtered reports pass through this node (${percentage.toFixed(1)}%)`}>
                <Chip
                    label={`${totalReports} of ${totalFilteredReports} reports (${percentage.toFixed(0)}%)`}
                    size="small"
                    color="default"
                    variant="filled"
                    sx={{ height: 24, fontSize: '0.7rem' }}
                />
            </Tooltip>

            {/* AI Mappings Chip */}
            <Tooltip title={`${aiMappingsCount} AI-generated mappings`}>
                <Chip
                    label={`AI: ${aiMappingsCount}`}
                    size="small"
                    color="info"
                    variant={aiMappingsCount > 0 ? 'filled' : 'outlined'}
                    sx={{ height: 24, fontSize: '0.7rem' }}
                />
            </Tooltip>

            {/* Manual Mappings Chip */}
            <Tooltip title={`${manualMappingsCount} manual mappings`}>
                <Chip
                    label={`Manual: ${manualMappingsCount}`}
                    size="small"
                    color="secondary"
                    variant={manualMappingsCount > 0 ? 'filled' : 'outlined'}
                    sx={{ height: 24, fontSize: '0.7rem' }}
                />
            </Tooltip>
        </Stack>
    );
};

export default React.memo(NodeStatsSummary);